import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
//Limitless imports
import { ClientContext } from "@limitless/common/utils";
import { useWebSockets } from "@limitless/common/hooks/web-sockets/WebSocketsContext";
import { useResources } from "@limitless/common/hooks/platform-resource/PlatformResourceContext";
import {
    useCustomerContext,
    useCustomerMessageContext,
    useGroupResponseTimeContext,
    useCustomerHistoryContext
} from "@limitless/customer/hooks";
import { createLLMWindowObject } from "utils/LLMWindowObject";
import { getLLMResources } from "utils/LLMResourceHelper";
import { log as pilog } from "@limitless/common/utils";

const AppVisibilityContext = createContext({});

export const AppVisibilityContextProvider = ({ children }) => {
    const hook = useAppVisibility();

    const context = useMemo(
        () => ({
            ...hook
        }),
        [hook.appVisibility, hook.unReadMessage]
    );

    return <AppVisibilityContext.Provider value={context}>{children}</AppVisibilityContext.Provider>;
};

export const useAppVisibilityContext = () => {
    return useContext(AppVisibilityContext);
};

AppVisibilityContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};

/**
 * Mobile Detection hook only accessed via the context wrapper so isMobileDevice can be made across the whole app
 */
const useAppVisibility = () => {
    const [appVisibility, setAppVisibility] = React.useState({
        isAppVisible: typeof ClientContext._appVisible === "string" ? true : ClientContext._appVisible,
        isAppOpen: false,
        hasOpened: false
    });
    const [unReadMessage, setUnReadMessage] = React.useState(false);
    const [lastMessageUpdate, setLastMessageUpdate] = React.useState(null);
    const [hasConnectedToWS, setHasConnectedtoWS] = React.useState(false);
    const [isFabVisible, setFabVisible] = React.useState(true);
    const { clearQuestion, customer } = useCustomerContext();
    const { message } = useCustomerMessageContext();
    const { loadResponseTime } = useGroupResponseTimeContext();
    const { connectToWebSocket } = useWebSockets();
    const { loadHistory } = useCustomerHistoryContext();
    const { loadAppResources } = useResources();
    const log = pilog.getLogger("LiveMessenger events");
    
    const toggleAppOpen = () => {
        // check if we have an unread message
        if (unReadMessage) {
            setUnReadMessage(false);
        }
        log.info("LiveMessenger", { open: !appVisibility.isAppOpen });
        setAppVisibility({ isAppVisible: true, isAppOpen: !appVisibility.isAppOpen, hasOpened: true });
    };

    const toggleAppVisibility = isVisible => {
        setAppVisibility({ ...appVisibility, isAppVisible: isVisible, isAppOpen: false });
    };

    const openApp = () => {
        setAppVisibility({ isAppVisible: true, isAppOpen: true, hasOpened: true });
    };

    const clearUnreadMessage = () => {
        setUnReadMessage(false);
    };

    const switchCardSet = cardSetId => {
        loadAppResources(null, getLLMResources(cardSetId));
    };

    React.useEffect(() => {
        switchCardSet(ClientContext._hiwSet ?? "");
    }, [ClientContext._hiwSet]);

    React.useEffect(() => {
        const isValidQueryParams = ClientContext.validateQueryParameters("messageId", "accessKey");

        // Check if _appVisible is false and not having any query parameters
        // For the new architecture changes related to fetching div tags from config.json
        if (ClientContext._appVisible === false && !isValidQueryParams) {
            toggleAppVisibility(false);
        }

        if (typeof ClientContext._appVisible === "string") {
            openApp();
        }
        // check when fab button is hidden then open LLM Window
        if (ClientContext._fabButtonStyle === "hidden") {
            setFabVisible(false);
            if (ClientContext._appVisible) {
                openApp();
            }
        }

        createLLMWindowObject(appVisibility, toggleAppVisibility, openApp, switchCardSet);
    }, [ClientContext._appVisible]);

    React.useEffect(() => {
        //if we close the App, clear the current question due to old requirements..
        //and save the last time the current message was updated
        if (!appVisibility.isAppOpen && appVisibility.hasOpened) {
            clearQuestion();
            setLastMessageUpdate(message.updated);
        }

        if (appVisibility.isAppOpen) {
            loadResponseTime();
            if (!hasConnectedToWS) {
                setHasConnectedtoWS(true);
                connectToWebSocket();
            }
        }
    }, [appVisibility.isAppOpen]);

    React.useEffect(() => {
        //if the app isn't open and a new dialogue comes in, set the unread flag
        if (!appVisibility.isAppOpen && lastMessageUpdate < message.updated) {
            setUnReadMessage(true);
        }

        const lastExpertDailogues = message.dialogues.filter(
            item => item.origin === "expert" && item.created > message.lastCustomerDialogueDate
        );

        if (lastExpertDailogues.length > 0 && typeof window.LLM.onMessageUpdate === "function") {
            window.LLM.onMessageUpdate({
                messageId: message.messageId,
                newExpertDialogueCount: lastExpertDailogues.length,
                isAppOpen: appVisibility.isAppOpen
            });
        }
    }, [message.updated]);

    React.useEffect(() => {
        if (customer.customerEmailHash && appVisibility.isAppOpen) {
            loadHistory();
        }
    }, [customer.customerEmailHash, appVisibility.isAppOpen]);

    return {
        appVisibility,
        toggleAppOpen,
        unReadMessage,
        clearUnreadMessage,
        toggleAppVisibility,
        isFabVisible
    };
};
